import "cross-fetch/polyfill";
import { Amplify as Auth } from "aws-amplify";
import {
  confirmResetPassword as amplifyConfirmResetPassword,
  confirmSignUp as amplifyConfirmSignUp,
  fetchAuthSession,
  fetchUserAttributes,
  getCurrentUser,
  resendSignUpCode,
  resetPassword as amplifyResetPassword,
  signIn as amplifySignIn,
  signInWithRedirect,
  signOut as amplifySignOut,
  signUp as amplifySignUp,
} from "aws-amplify/auth";
import {
  CookieStorage as AmplifyCookieStorage,
  Hub,
  sessionStorage,
} from "aws-amplify/utils";
import { cognitoUserPoolsTokenProvider } from "aws-amplify/auth/cognito";
import { isLocal } from "fe-common/utils/envGetter";

let attributesCache = {};
const invalidateAttributesCache = () => (attributesCache = {});

const userPoolCredentials = {
  ClientId: "57iq31dm0i58nb48a02vbmu12s",
  UserPoolId: "us-east-1_whq6QJZZw",
};

Auth.configure({
  Auth: {
    Cognito: {
      // (required) only for Federated Authentication - Amazon Cognito Identity Pool ID
      identityPoolId: "us-east-1:5da25eda-0f3f-4ff8-9469-6a3cfc1208b6",
      // (optional) - Amazon Cognito User Pool ID
      userPoolId: userPoolCredentials.UserPoolId,
      // (optional) - Amazon Cognito Web Client ID (26-char alphanumeric string, App client secret needs to be disabled)
      userPoolClientId: userPoolCredentials.ClientId,
      // (optional) - Hosted UI configuration
      loginWith: {
        oauth: {
          domain: "auth.kahun.com",
          scopes: ["email", "openid", "aws.cognito.signin.user.admin"],
          redirectSignIn: [window.location.origin],
          redirectSignOut: [window.location.origin],
          responseType: "code",
          providers: ["Google"],
        },
      },
    },
  },
});
cognitoUserPoolsTokenProvider.setKeyValueStorage(
  new AmplifyCookieStorage(
    new AmplifyCookieStorage({
      domain: isLocal() ? "localhost" : ".kahun.com",
    }),
  ),
);

export enum UserAttrs {
  SPECIALITY = "custom:specialty",
}

export const updateUserStorageSetting = (opt: {
  useSessionStorage: boolean;
}) => {
  if (opt.useSessionStorage) {
    cognitoUserPoolsTokenProvider.setKeyValueStorage(sessionStorage);
  }
};

const getErrorMsg = (err) => {
  const errorMsg = err.message || JSON.stringify(err);
  console.error(errorMsg);
  return errorMsg;
};

//This function resolves to a method which can be used to resend confirmation code.
export const signUp = async (
  email: string,
  password: string,
  speciality: string,
  partner: string,
  customFields?: object,
  clientData?: Record<string, string>,
): Promise<(() => {}) | null> => {
  invalidateAttributesCache();
  return new Promise(async (resolve, reject) => {
    const hosturl =
      window.location.hostname !== "localhost"
        ? window.location.hostname
        : "develop.kahun.com/clinic_manage";

    try {
      const { nextStep } = await amplifySignUp({
        username: email.toLowerCase(),
        password,
        options: {
          userAttributes: {
            email: email.toLowerCase(),
            "custom:specialty": speciality,
            "custom:partner": partner,
            ...customFields,
          },
          clientMetadata: {
            verifyUrl: `https://${hosturl}/verify`,
            ...(clientData || {}),
          },
          // optional
          autoSignIn: true,
        },
      });
      if (nextStep.signUpStep === "CONFIRM_SIGN_UP") {
        resolve(() => () =>
          resendSignUpCode({ username: email.toLowerCase() }),
        );
      } else {
        resolve(null);
      }
    } catch (e) {
      reject(getErrorMsg(e));
    }
  });
};

export const getResendConfirmationCodeFn = (
  email: string,
): Promise<() => {}> => {
  return new Promise((resolve) => {
    resolve(() => () => resendSignUpCode({ username: email.toLowerCase() }));
  });
};

export const confirmSignUp = (email: string, confirmationCode: string) => {
  return new Promise(async (resolve, reject) => {
    try {
      const { isSignUpComplete } = await amplifyConfirmSignUp({
        username: email,
        confirmationCode,
      });
      if (isSignUpComplete) resolve(true);
      else reject("sign up failed");
    } catch (e) {
      reject(getErrorMsg(e));
    }
  });
};

const subscribersCb = [];
const listener = (data) => {
  switch (data.payload.event) {
    case "signedIn": {
      subscribersCb.forEach((s) => s());
      break;
    }
  }
};
Hub.listen("auth", listener);

export const registerOnSignIn = (onSignIn: () => void) => {
  subscribersCb.push(onSignIn);
  isValidSession().then((valid) => {
    if (valid) {
      onSignIn();
    }
  });
};

export const socialLoginRedirectItem = "socialLoginRedirect";
export const authenticateUserGoogle = async (socialLoginRedirect?: string) => {
  if (await isValidSession()) await signOut();
  await sessionStorage.setItem(
    socialLoginRedirectItem,
    socialLoginRedirect || "",
  );
  return signInWithRedirect({
    provider: "Google" as any,
  });
};

export enum SIGN_IN_STATUS {
  DONE,
  RESET_PASSWORD,
  CONFIRM_SIGN_IN_MISSING,
}
export const authenticateUser = (email: string, password: string) => {
  return new Promise(async (resolve, reject) => {
    if (await isValidSession()) await signOut();
    invalidateAttributesCache();
    try {
      const { nextStep } = await amplifySignIn({ username: email, password });
      switch (nextStep.signInStep) {
        case "DONE":
          resolve(SIGN_IN_STATUS.DONE);
          break;
        case "RESET_PASSWORD": //we don't have this flow currently and we can use forgot password instead
          reject(SIGN_IN_STATUS.RESET_PASSWORD);
          break;
        default:
          reject(SIGN_IN_STATUS.CONFIRM_SIGN_IN_MISSING);
      }
    } catch (e) {
      reject(getErrorMsg(e));
    }
  });
};

export const forgotPassword = (email: string): Promise<void> => {
  return new Promise(async (resolve, reject) => {
    try {
      await amplifyResetPassword({ username: email });
      resolve();
    } catch (e) {
      reject(getErrorMsg(e));
    }
  });
};

export const completeForgotPassword = (
  email: string,
  confirmationCode: string,
  newPassword: string,
): Promise<void> => {
  return new Promise(async (resolve, reject) => {
    try {
      await amplifyConfirmResetPassword({
        username: email,
        confirmationCode,
        newPassword,
      });
      resolve();
    } catch (e) {
      reject(getErrorMsg(e));
    }
  });
};

export const signOut = async () => {
  invalidateAttributesCache();
  await amplifySignOut().catch((e) => console.error(e));
};

export const isValidSession = (): Promise<boolean> => {
  return new Promise(async (resolve) => {
    try {
      await getCurrentUser();
      resolve(true);
    } catch (e) {
      resolve(false);
    }
  });
};

export const refreshSession = async (): Promise<boolean> => {
  try {
    await fetchAuthSession({ forceRefresh: true });
    return true;
  } catch (e) {
    return false;
  }
};

export const getAccessToken = (): Promise<string> => {
  return new Promise(async (resolve, reject) => {
    try {
      const { accessToken } =
        (await fetchAuthSession({ forceRefresh: false })).tokens ?? {};
      resolve(accessToken.toString());
    } catch (err) {
      console.log(err);
      reject(err);
    }
  });
};

export const getIdToken = (): Promise<string> => {
  return new Promise(async (resolve, reject) => {
    try {
      const { idToken } =
        (await fetchAuthSession({ forceRefresh: false })).tokens ?? {};
      resolve(idToken.toString());
    } catch (err) {
      console.log(err);
      reject(err);
    }
  });
};

const NO_ROLES = [];
export const getUserRoles = (): Promise<string[]> => {
  return new Promise(async (resolve) => {
    let idToken;
    try {
      idToken = (await fetchAuthSession()).tokens?.idToken;
    } catch (e) {}

    if (!idToken) {
      resolve(NO_ROLES);
    } else {
      resolve(idToken.payload["cognito:groups"] || NO_ROLES);
    }
  });
};

export function getCurrentUserEmail() {
  try {
    return getCurrentUserAttr("email");
  } catch (e) {
    return Promise.resolve("");
  }
}

export const getCurrentUserAttr = (attribute: string): Promise<string> => {
  return new Promise(async (resolve) => {
    if (attributesCache[attribute] || attributesCache[attribute] === "") {
      resolve(attributesCache[attribute]);
      return;
    }

    const isValid = await isValidSession();
    if (!isValid) {
      resolve("");
    } else {
      const attrs = await fetchUserAttributes();
      attributesCache = { ...attributesCache, ...attrs };
      if (!attrs[attribute]) {
        attributesCache[attribute] = "";
      }
      const val = attrs[attribute];
      resolve(val);
    }
  });
};
